/**
 * DeliveryNoteScanPage — Phase 4c.
 *
 * Lieferschein-PDF oder Foto hochladen → Gemini extrahiert Positionen
 * (Lieferant, LS-Nr, Artikel + Menge + EK) → User wählt pro Position
 * "Match" (bestehender Artikel), "Create" (neuer Artikel) oder "Skip"
 * → Sammelbuchung erhöht Lagerbestand + erzeugt `stock_movements` Einträge.
 *
 * Route: /dealer/wms/scan-delivery
 */
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Upload, ScanLine, Check, Plus, X as XIcon, Loader2, ArrowLeft, AlertCircle, ShoppingCart, Printer,
} from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import useScanBridge from "../lib/useScanBridge";
import { scanFolderSupported, getScanFolder, waitForNewScan } from "../lib/scanFolder";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ScannerSetupHelp from "../components/qz/ScannerSetupHelp";
import DocumentCameraCapture from "../components/wms/DocumentCameraCapture";
import DeliveryScanQrCard from "../components/wms/DeliveryScanQrCard";
import ScanHistory from "../components/wms/ScanHistory";
import DeskScannerConfigModal from "../components/wms/DeskScannerConfigModal";
import { runScanJob } from "../lib/scanJobs";
import { ScanProgress } from "../components/ScanProgress";
import { ScanPagesPreview } from "../components/ScanPagesPreview";
import { FolderOpen, Wrench, Settings, FileText } from "lucide-react";
import { takeHandoffFile, runHandoffCleanup } from "../lib/fileHandoff";
import WmsSettingsModal from "../components/dealer/WmsSettingsModal";
import { useGearsVisible } from "../lib/gearVisibility";
import { CevaWarrantyPanel } from "../components/wms/CevaWarrantyPanel";
import { CommissionOrderPanel } from "../components/wms/CommissionOrderPanel";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function DeliveryNoteScanPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [file, setFile] = useState(null);
    const [scan, setScan] = useState(null);
    const [rows, setRows] = useState([]);
    const [supplierName, setSupplierName] = useState("");
    const [dnNumber, setDnNumber] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [commissionMode, setCommissionMode] = useState(false);
    const [commissionTarget, setCommissionTarget] = useState(null); // {type,id,name} | null (=Konsignationsverwaltung)
    const [decisionRequired, setDecisionRequired] = useState(false); // Empfänger ≠ eigene Firmierung ODER unklar → aktive Wahl nötig
    const [decisionMade, setDecisionMade] = useState(false);
    const [docType, setDocType] = useState("delivery_note"); // delivery_note | invoice | combined
    const [historyReload, setHistoryReload] = useState(0);
    const [deskModal, setDeskModal] = useState(false);
    const [deskScanner, setDeskScanner] = useState(null);
    // Jul-2026 (User-Wunsch) — Zahnrad: Lieferschein-Regeln & Anreicherungs-Einstellungen
    // (gleiches Modal wie in der Warenwirtschaft) direkt auf dieser Seite.
    const [wmsSettingsOpen, setWmsSettingsOpen] = useState(false);
    const gearsOk = useGearsVisible();
    // Jul-2026 (User-Wunsch) — Standort-Zuordnung des Scans anzeigen & korrigierbar machen
    const [orgSites, setOrgSites] = useState([]);
    const [targetDealerId, setTargetDealerId] = useState("");

    useEffect(() => {
        api.get("/org/me").then((r) => {
            const d = r.data || {};
            const list = [];
            const push = (u, roleLabel) => {
                if (u && u.id && !list.some((x) => x.id === u.id)) {
                    list.push({ id: u.id, label: [u.company_name || u.name, u.site_code ? `(${u.site_code})` : "", u.city || ""].filter(Boolean).join(" "), role: roleLabel });
                }
            };
            push(d.hauptsitz, "Hauptsitz");
            push(d.self, d.branch_role === "filiale" ? "Filiale" : "Hauptsitz");
            (d.branches || []).forEach((b) => push(b, "Filiale"));
            setOrgSites(list);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        api.get("/wms/desk-scanner").then((r) => setDeskScanner(r.data.config)).catch(() => {});
    }, []);
    // Jul-2026 (User-Wunsch): aus dem Dokumenten-Postausgang übergebene Datei
    // automatisch scannen („Zum Lieferschein-Scan"-Weiterverarbeitung).
    useEffect(() => {
        const f = takeHandoffFile();
        if (f) { setFile(f); onScan(f); toast.info(`„${f.name}" aus dem Postausgang übernommen — KI-Analyse läuft…`); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [busy, setBusy] = useState(false);
    const [scanPhase, setScanPhase] = useState("upload");
    const [scanPages, setScanPages] = useState(null);
    const [scanError, setScanError] = useState("");
    const [result, setResult] = useState(null);
    const [dupBlock, setDupBlock] = useState(null);
    // Aug-2026 (User-Vorschlag): Nach Automatik-Verbuchung nur noch EIN Button
    // „Änderungen speichern & Korrektur buchen" — Original-Stand merken.
    const originalRowsRef = useRef(null);
    const [correcting, setCorrecting] = useState(false);
    // Jul-2026 (User-Wunsch „Sammel-Modus") — Stapel-Warteschlange (früh
    // deklariert, da Effects weiter unten darauf zugreifen).
    const [queue, setQueue] = useState([]);
    const qidRef = useRef(1);
    const [creatingSupplier, setCreatingSupplier] = useState(false);
    // Jul-2026 (User-Wunsch) — 1-Klick: erkannte Kd-Nr. + Lieferantenname direkt
    // als neuen Lieferanten anlegen, wenn noch keiner hinterlegt ist.
    const createSupplierFromScan = async () => {
        const name = (supplierName || scan?.supplier_name || "").trim();
        if (!name) { toast.error("Kein Lieferantenname erkannt — bitte oben im Feld Lieferant eintragen."); return; }
        setCreatingSupplier(true);
        try {
            const r = await api.post("/suppliers", { name, customer_number: scan.customer_number });
            setScan((s) => ({
                ...s,
                supplier_account_match: {
                    supplier_id: r.data.id,
                    supplier_name: r.data.name,
                    customer_number: r.data.customer_number,
                },
            }));
            toast.success(`Lieferant „${name}" mit Kd-Nr. ${scan.customer_number} angelegt und zugeordnet`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Lieferant konnte nicht angelegt werden");
        }
        setCreatingSupplier(false);
    };
    const [camOpen, setCamOpen] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const batchInputRef = useRef(null);
    // Jul-2026 (User-Wunsch) — 1-Klick-Direktscan wie in der Werkstatt:
    // Scanner über die lokale Scan-Bridge ansteuern → Datei geht sofort in den KI-Scan.
    const bridge = useScanBridge((f) => enqueueFiles([f]));

    // Aug-2026 (User-Wunsch): Duplex standardmäßig AUS, nach jedem Scan Auto-Reset.
    const [duplexScan, setDuplexScan] = useState(() => localStorage.getItem("tw_scan_duplex") === "1");
    const toggleDuplex = () => {
        setDuplexScan((p) => {
            localStorage.setItem("tw_scan_duplex", p ? "0" : "1");
            return !p;
        });
    };
    useEffect(() => {
        const onReset = () => setDuplexScan(false);
        window.addEventListener("tw-duplex-reset", onReset);
        return () => window.removeEventListener("tw-duplex-reset", onReset);
    }, []);
    useEffect(() => {
        const onPaste = (e) => {
            const item = Array.from(e.clipboardData?.items || [])
                .find((i) => i.type.startsWith("image/") || i.type === "application/pdf");
            const f = item?.getAsFile?.();
            if (f) { e.preventDefault(); enqueueFiles([f]); }
        };
        document.addEventListener("paste", onPaste);
        return () => document.removeEventListener("paste", onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scan, busy, result, queue]);

    // Jul-2026 (User-Report FRAL) — Scan-Ordner-Methode wie in der bewährten
    // Werkstatt-„Scan-Erfassung": die Scanner-Software legt die Datei in einen
    // Ordner, die App wartet darauf und verarbeitet sie automatisch.
    const [folderWaiting, setFolderWaiting] = useState(false);
    const folderAbortRef = useRef(null);
    const startFolderScan = async () => {
        if (!scanFolderSupported()) {
            toast.error("Dieser Browser unterstützt keinen Scan-Ordner — bitte Datei hochladen.");
            return;
        }
        try {
            const handle = await getScanFolder();
            if (!handle) return;
            const ctrl = new AbortController();
            folderAbortRef.current = ctrl;
            setFolderWaiting(true);
            const f = await waitForNewScan(handle, { sinceMs: Date.now() - 3000, signal: ctrl.signal });
            setFolderWaiting(false);
            if (f) { enqueueFiles([f]); }
            else if (!ctrl.signal.aborted) toast.error("Kein neuer Scan im Ordner gefunden (90 s). Bitte erneut versuchen.");
        } catch (e) {
            setFolderWaiting(false);
            if (e?.name !== "AbortError") toast.error("Scan-Ordner konnte nicht verbunden werden");
        }
    };

    // QR/Handy-Aufruf (?cam=1) → Kamera-Aufnahme direkt starten
    useEffect(() => {
        if (searchParams.get("cam") === "1") setCamOpen(true);
    }, [searchParams]);

    // Jul-2026 (User-Wunsch „Sammel-Modus") — mehrere Belege hintereinander
    // scannen: jeder Beleg startet SOFORT seinen eigenen Hintergrund-Job,
    // der Stapel wird ohne Wartezeit parallel analysiert.
    const validateFile = (f) => {
        if (!f) return false;
        const okType = /pdf$|^image\//i.test(f.type) || /\.(pdf|png|jpe?g|webp)$/i.test(f.name);
        if (!okType) { toast.error(`${f.name || "Datei"}: Nur PDF oder Bilddateien erlaubt`); return false; }
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name || "Datei"}: zu groß (max 10 MB)`); return false; }
        return true;
    };

    const startBackground = (f) => {
        const qid = qidRef.current;
        qidRef.current += 1;
        setQueue((q) => [...q, { qid, name: f.name || "Scan", file: f, status: "running" }]);
        runScanJob("delivery-note", f, {
            params: { origin: "scan_page" },
            onStatus: (job) => {
                const p = job?.progress;
                if (p?.pages_total > 1) {
                    setQueue((q) => q.map((en) => (en.qid === qid ? { ...en, progress: p } : en)));
                }
            },
        })
            .then((data) => {
                // Aug-2026: ein Scan kann MEHRERE Belege enthalten (ADF-Stapel)
                if (data?.multi && Array.isArray(data.documents)) {
                    setQueue((q) => q.flatMap((en) => (en.qid === qid
                        ? data.documents.map((doc, i) => ({
                            qid: i === 0 ? qid : (qidRef.current + i),
                            name: doc.source_file_name || `${en.name} — Beleg ${i + 1}/${data.documents.length}`,
                            file: en.file, status: "done", result: doc,
                        }))
                        : [en])));
                    qidRef.current += data.documents.length;
                    toast.success(`${data.documents.length} Belege im Scan erkannt und getrennt`);
                    return;
                }
                setQueue((q) => q.map((en) => (en.qid === qid ? { ...en, status: "done", result: data } : en)));
            })
            .catch((err) => setQueue((q) => q.map((en) => (en.qid === qid
                ? { ...en, status: "error", error: err.message || "Scan fehlgeschlagen" } : en))));
    };

    const enqueueFiles = (fileList) => {
        const files = Array.from(fileList || []).filter(validateFile);
        if (!files.length) return;
        let rest = files;
        if (!scan && !busy && !result) {
            setFile(files[0]);
            onScan(files[0]);
            rest = files.slice(1);
        }
        rest.forEach(startBackground);
        if (rest.length) toast.info(`${rest.length} Beleg(e) im Stapel — die KI analysiert parallel weiter.`);
    };

    const pickDropped = (fileList) => enqueueFiles(fileList);

    // Jul-2026 (User-Wunsch „Stapel Auto-Buchen") — eindeutig = eigener
    // Empfänger, kein Nicht-Lager-Beleg, kein Doppel-Scan-Verdacht, KI sicher
    // und ALLE Positionen einem Artikel zugeordnet.
    const isUnambiguous = (res) => !!res && !res.mocked && !res.non_stock
        && !res.commission_decision_required && !res.is_commission
        && res.recipient_match === "own" && !res.possible_duplicate
        && (res.items || []).length > 0 && (res.items || []).every((m) => m.match);

    const autoBookAll = async () => {
        const eligible = queue.filter((en) => en.status === "done" && isUnambiguous(en.result));
        if (!eligible.length) return;
        if (!window.confirm(`${eligible.length} eindeutige(n) Beleg(e) jetzt automatisch ins Lager buchen?`)) return;
        setQueue((q) => q.map((en) => (eligible.some((x) => x.qid === en.qid) ? { ...en, status: "booking" } : en)));
        let ok = 0;
        for (const en of eligible) {
            const res = en.result;
            try {
                const items = (res.items || []).map((m) => ({
                    action: "match",
                    article_id: m.match?.article_id,
                    sku: m.match?.sku || m.scanned?.sku || "",
                    name: m.scanned?.name || "",
                    quantity: Number(m.scanned?.quantity) || 0,
                    unit: m.scanned?.unit || "Stück",
                    purchase_price_net: Number(m.scanned?.unit_price_net || m.match?.purchase_price_net) || null,
                    uvp_gross: Number(m.scanned?.uvp_gross) || null,
                    reorder_item_id: m.reorder_match?.item_id || null,
                }));
                const r = await api.post("/articles/scan-delivery-note/commit", {
                    file_name: en.name, supplier_name: res.supplier_name,
                    delivery_note_number: res.delivery_note_number, items,
                    recipient_name: res.recipient_name,
                    is_commission: false, consignment_target: null,
                    doc_type: res.doc_type || "delivery_note",
                    origin: "scan_page",
                    fingerprint: res.fingerprint,
                    file_url: res.file_url || null,
                    file_storage_path: res.file_storage_path || null,
                    customer_number: res.customer_number || null,
                    invoice_number: res.invoice_number || null,
                    total_gross: res.total_gross || null,
                    shipping_net: res.shipping_net || null,
                    supplier_account_match: res.supplier_account_match || null,
                    vehicle_vins: res.vehicle_vins || [],
                    target_dealer_id: user?.employer_dealer_id || user?.id || null,
                    force: false,
                });
                ok += 1;
                setQueue((q) => q.map((x) => (x.qid === en.qid
                    ? { ...x, status: "booked", bookedInfo: `${r.data.count_booked} gebucht` } : x)));
            } catch (e) {
                const det = e.response?.data?.detail;
                const msg = det?.code === "possible_duplicate"
                    ? "Möglicher Doppel-Scan — bitte manuell prüfen"
                    : ((typeof det === "string" && det) || det?.message || "Buchung fehlgeschlagen");
                setQueue((q) => q.map((x) => (x.qid === en.qid ? { ...x, status: "error", error: msg } : x)));
            }
        }
        setHistoryReload((n) => n + 1);
        if (ok) toast.success(`${ok} Beleg(e) automatisch gebucht`);
    };

    if (user === false) navigate("/login");
    if (user && user.role !== "b2b" && user.role !== "admin" && user.role !== "zweiradverkaeufer") navigate("/");

    // Ergebnis eines Scans in die Prüf-Ansicht übernehmen (auch aus dem Stapel).
    const applyScanData = (data) => {
        setScan(data);
        // Aug-2026 (User-Report FBWE→HILD): Vorauswahl = EIGENER Standort (Scan-Ort),
        // NICHT das N+F-Konto — das Konto steuert nur die Buchhaltung.
        setTargetDealerId(user?.employer_dealer_id || user?.id || "");
        setSupplierName(data.supplier_name || "");
        setDnNumber(data.delivery_note_number || "");
        setRecipientName(data.recipient_name || "");
        setDocType(data.doc_type || "delivery_note");
        setCommissionMode(!!data.is_commission);
        setCommissionTarget((data.commission_suggestions || [])[0] || null);
        const needDecision = !!data.commission_decision_required;
        setDecisionRequired(needDecision);
        setDecisionMade(!needDecision); // bei „own" ist die Sache klar
        const initRows = (data.items || []).map((m) => ({
            idx: m.idx,
            // Aug-2026 (User-Wunsch Jörg): Rückstands-Positionen NICHT buchen —
            // beim Buchen wandern sie automatisch auf die Rückstands-/Bestellliste.
            action: data.non_stock ? "skip" : (m.match ? "match" : "create"),
            toOrder: true,
            article_id: m.match?.article_id || null,
            sku: m.match?.sku || m.scanned?.sku || "",
            name: m.scanned?.name || "",
            quantity: m.scanned?.quantity || 0,
            unit: m.scanned?.unit || "Stück",
            purchase_price_net: m.scanned?.unit_price_net || m.match?.purchase_price_net || 0,
            uvp_gross: m.scanned?.uvp_gross || null,
            _matchInfo: m.match,
            _reorder: m.reorder_match || null,
            _backorder: !!m.backorder,
            reorder_item_id: m.reorder_match?.item_id || null,
        }));
        setRows(initRows);
        originalRowsRef.current = initRows.map((r) => ({ ...r }));
        if (data.mocked) {
            toast.warning(`Erkennung erfolgte ohne KI (${data.reason || "Service nicht erreichbar"}). Positionen bitte manuell ergänzen.`);
        } else {
            toast.success(`${initRows.length} Position(en) erkannt`);
        }
    };

    const onScan = async (fileArg) => {
        const theFile = fileArg instanceof File ? fileArg : file;
        if (!theFile) { toast.error("Bitte Datei wählen"); return; }
        setBusy(true);
        setResult(null);
        setScanError("");
        setScanPages(null);
        try {
            // Jul-2026 (Bugfix Cloudflare-Timeout) — Verarbeitung als Hintergrund-
            // Job mit Polling statt einer langen HTTP-Anfrage.
            const data = await runScanJob("delivery-note", theFile, {
                params: { origin: "scan_page" },
                onPhase: setScanPhase,
                onStatus: (job) => setScanPages(job?.progress || null),
            });
            // Handoff aus dem Postausgang: Quelldatei erst JETZT (Erfolg) aus
            // dem überwachten Ordner löschen.
            runHandoffCleanup(theFile);
            // Aug-2026: ADF-Stapel mit mehreren Belegen → ersten anzeigen,
            // Rest in die Stapel-Warteschlange legen.
            if (data?.multi && Array.isArray(data.documents) && data.documents.length > 0) {
                const [first, ...rest] = data.documents;
                if (rest.length) {
                    setQueue((q) => [...q, ...rest.map((doc, i) => ({
                        qid: qidRef.current + i,
                        name: doc.source_file_name || `${theFile.name || "Scan"} — Beleg ${i + 2}/${data.documents.length}`,
                        file: theFile, status: "done", result: doc,
                    }))]);
                    qidRef.current += rest.length;
                    toast.success(`${data.documents.length} Belege im Scan erkannt — Rest liegt im Stapel`);
                }
                applyScanData(first);
            } else {
                applyScanData(data);
            }
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Scan fehlgeschlagen";
            setScanError(typeof msg === "string" ? msg : "Scan fehlgeschlagen");
            toast.error(typeof msg === "string" ? msg : "Scan fehlgeschlagen");
        }
        setBusy(false);
    };

    // Sammel-Modus: sobald die Prüf-Ansicht frei ist, nächsten fertigen
    // Beleg aus dem Stapel automatisch laden.
    useEffect(() => {
        if (scan || busy || result) return;
        const next = queue.find((en) => en.status === "done");
        if (next) {
            setQueue((q) => q.filter((en) => en.qid !== next.qid));
            setFile(next.file);
            setScanError("");
            applyScanData(next.result);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queue, scan, busy, result]);

    const updateRow = (idx, patch) => {
        setRows((rs) => rs.map((r) => r.idx === idx ? { ...r, ...patch } : r));
    };

    const onCommit = async (force = false) => {
        const items = rows.filter((r) => r.action !== "skip" && r.quantity > 0).map((r) => ({
            action: r.action,
            article_id: r.article_id,
            sku: r.sku,
            name: r.name,
            quantity: Number(r.quantity),
            unit: r.unit,
            purchase_price_net: Number(r.purchase_price_net) || null,
            uvp_gross: Number(r.uvp_gross) || null,
            reorder_item_id: r.reorder_item_id || null,
        }));
        if (items.length === 0 && docType === "delivery_note") { toast.error("Keine Positionen zum Buchen ausgewählt"); return; }
        if (items.length === 0 && !window.confirm("Keine Lager-Positionen ausgewählt — Beleg nur an die Buchhaltung übergeben (keine Bestandsbuchung)?")) return;
        if (decisionRequired && !decisionMade) {
            toast.error("Bitte zuerst wählen: Kommissionsware oder eigenes Lager.");
            return;
        }
        const confirmMsg = commissionMode
            ? `${items.length} Position(en) als KOMMISSIONSWARE buchen${commissionTarget ? ` (Ziel: ${commissionTarget.name})` : " (Konsignationsverwaltung)"}?`
            : `${items.length} Position(en) ins Lager buchen?`;
        if (!force && !window.confirm(confirmMsg)) return;
        setBusy(true);
        try {
            const r = await api.post("/articles/scan-delivery-note/commit", {
                file_name: file?.name, supplier_name: supplierName,
                delivery_note_number: dnNumber, items,
                recipient_name: recipientName,
                is_commission: commissionMode,
                consignment_target: commissionMode ? commissionTarget : null,
                doc_type: docType,
                origin: "scan_page",
                fingerprint: scan?.fingerprint,
                file_url: scan?.file_url || null,
                file_storage_path: scan?.file_storage_path || null,
                customer_number: scan?.customer_number || null,
                invoice_number: scan?.invoice_number || null,
                total_gross: scan?.total_gross || null,
                shipping_net: scan?.shipping_net || null,
                supplier_account_match: scan?.supplier_account_match || null,
                vehicle_vins: scan?.vehicle_vins || [],
                target_dealer_id: targetDealerId || null,
                force,
            });
            setResult(r.data);
            setHistoryReload((n) => n + 1);
            toast.success(
                (commissionMode ? "Kommissionsware: " : "") +
                `${r.data.count_booked} gebucht, ${r.data.count_created} neu angelegt` +
                (r.data.forwarded_to_accounting ? " · an Buchhaltung übergeben" : ""));
        } catch (e) {
            const det = e.response?.data?.detail;
            if (e.response?.status === 409 && det?.code === "possible_duplicate") {
                // Aug-2026 (User-Wunsch): klare Meldung + „Scan verwerfen"-Option
                setBusy(false);
                setDupBlock({ ...(det.duplicate || {}), message: det.message });
                return;
            }
            toast.error((typeof det === "string" && det) || det?.message || "Buchung fehlgeschlagen");
        }
        setBusy(false);
    };

    const reset = () => { setFile(null); setScan(null); setRows([]); setResult(null); setDecisionRequired(false); setDecisionMade(false); setCommissionMode(false); setDupBlock(null); originalRowsRef.current = null; };

    // Aug-2026 (User-Vorschlag): geänderte Positionen nach Automatik-Verbuchung
    // als Mengen-Korrektur buchen (kein erneutes Voll-Buchen = kein Doppelbestand).
    const effQty = (r) => (r.action === "skip" ? 0 : parseFloat(r.quantity) || 0);
    const changedRows = (scan?.auto_booked && originalRowsRef.current)
        ? rows.filter((r) => {
            const o = originalRowsRef.current.find((x) => x.idx === r.idx);
            return o && Math.abs(effQty(r) - effQty(o)) > 0.0001;
        })
        : [];
    const bookCorrections = async () => {
        if (!changedRows.length) return;
        setCorrecting(true);
        try {
            const items = changedRows.map((r) => {
                const o = originalRowsRef.current.find((x) => x.idx === r.idx);
                return { article_id: r.article_id || null, sku: r.sku || null, name: r.name,
                    old_quantity: effQty(o), new_quantity: effQty(r) };
            });
            const { data } = await api.post("/wms/scan-corrections/rebook", {
                reference: `Korrektur ${dnNumber || scan?.delivery_note_number || "Scan"}`, items,
            });
            const n = (data.adjusted || []).length;
            toast.success(`${n} Korrektur(en) gebucht${(data.skipped || []).length ? ` — ${data.skipped.length} ohne Artikel-Treffer übersprungen` : ""}`);
            originalRowsRef.current = rows.map((r) => ({ ...r }));
            setHistoryReload((x) => x + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Korrektur fehlgeschlagen");
        }
        setCorrecting(false);
    };

    const discardScan = () => {
        reset();
        toast.success("Scan verworfen — bereit für den nächsten Beleg");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div className="min-h-screen bg-gray-50" data-testid="delivery-scan-page">
            <WmsSettingsModal open={wmsSettingsOpen} onClose={() => setWmsSettingsOpen(false)} />
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <div className="flex items-center gap-4 flex-wrap">
                        <Link to="/dealer/wms" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-wms">
                            <ArrowLeft className="w-3 h-3" /> Zurück zur Warenwirtschaft
                        </Link>
                        <Link to="/dealer/documents"
                            className="inline-flex items-center gap-2 text-xs font-bold bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 rounded-sm px-3 py-1.5 transition-colors"
                            data-testid="link-document-inbox">
                            <FolderOpen className="w-4 h-4" /> Zum Dokumenten-Center (Ordner-Überwachung)
                        </Link>
                    </div>
                    <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <ScanLine className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <h1 className="font-display font-black text-4xl md:text-5xl inline-flex items-center gap-3">
                                    Lieferschein scannen
                                    {gearsOk && (
                                        <button type="button" onClick={() => setWmsSettingsOpen(true)}
                                            className="p-2 rounded-sm border border-gray-600 text-gray-300 hover:text-white hover:border-white transition-colors"
                                            title="Einstellungen: Lieferschein-Regeln & KI-Artikeldaten-Anreicherung"
                                            data-testid="delivery-scan-settings-gear">
                                            <Settings className="w-5 h-5" />
                                        </button>
                                    )}
                                </h1>
                                <p className="text-sm text-gray-300 mt-1">PDF/Foto hochladen, per Kamera aufnehmen oder Handy-QR → KI erkennt Positionen → ein Klick zum Buchen</p>
                            </div>
                        </div>
                        {/* Aug-2026 (User-Wunsch Jörg): QR auch am Handy zeigen —
                            Doppelklick/Doppel-Tipp öffnet dort direkt die Kamera. */}
                        <div className="shrink-0">
                            <DeliveryScanQrCard onMobileScan={() => setCamOpen(true)} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
                {/* Jul-2026 (User-Wunsch) — Sammel-Modus: Stapel-Leiste mit Live-Status */}
                {(queue.length > 0 || (scan && !result)) && (
                    <div className="bg-white border-l-4 border-[#FFCC00] border border-gray-200 rounded-sm p-3" data-testid="scan-queue-panel">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-xs font-black uppercase tracking-wider text-[#111827] inline-flex items-center gap-2">
                                <ScanLine className="w-4 h-4" /> Stapel-Verarbeitung
                                {queue.length > 0 && (
                                    <span className="normal-case tracking-normal font-bold text-gray-600" data-testid="scan-queue-counts">
                                        — {queue.filter((e) => e.status === "running").length} in Analyse · {queue.filter((e) => e.status === "done").length} fertig · {queue.filter((e) => e.status === "booked").length} gebucht · {queue.filter((e) => e.status === "error").length} Fehler
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {queue.filter((en) => en.status === "done" && isUnambiguous(en.result)).length > 0 && (
                                    <Button size="sm" onClick={autoBookAll}
                                        className="h-8 rounded-sm text-xs font-black bg-green-600 hover:bg-green-700 text-white" data-testid="scan-queue-autobook-btn">
                                        <Check className="w-3.5 h-3.5 mr-1" />
                                        {queue.filter((en) => en.status === "done" && isUnambiguous(en.result)).length} eindeutige automatisch buchen
                                    </Button>
                                )}
                                <input ref={batchInputRef} type="file" multiple className="hidden"
                                    accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                                    onChange={(e) => { pickDropped(e.target.files); e.target.value = ""; }}
                                    data-testid="scan-queue-file-input" />
                                <Button size="sm" variant="outline" onClick={() => batchInputRef.current?.click()}
                                    className="h-8 rounded-sm text-xs font-bold border-2 border-[#111827]" data-testid="scan-queue-add-btn">
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Weitere Belege
                                </Button>
                                <Button size="sm" onClick={() => bridge.start()} disabled={bridge.scanning}
                                    className="h-8 rounded-sm text-xs font-black bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827]" data-testid="scan-queue-scan-btn">
                                    {bridge.scanning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ScanLine className="w-3.5 h-3.5 mr-1" />}
                                    Weiter scannen
                                </Button>
                            </div>
                        </div>
                        {queue.length > 0 && (
                            <ul className="mt-2 divide-y divide-gray-100" data-testid="scan-queue-list">
                                {queue.map((en) => (
                                    <li key={en.qid} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`scan-queue-item-${en.qid}`}>
                                        {en.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600 shrink-0" />}
                                        {en.status === "booking" && <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600 shrink-0" />}
                                        {en.status === "done" && <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />}
                                        {en.status === "booked" && <Check className="w-3.5 h-3.5 text-green-700 shrink-0" />}
                                        {en.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                                        <span className="font-bold truncate">{en.name}</span>
                                        <span className="text-gray-500">
                                            {en.status === "running" && (en.progress?.pages_total > 1
                                                ? `KI analysiert… Seite ${Math.min((en.progress.pages_done || 0) + 1, en.progress.pages_total)} von ${en.progress.pages_total}`
                                                : "KI analysiert…")}
                                            {en.status === "booking" && "wird gebucht…"}
                                            {en.status === "done" && `fertig — ${(en.result?.supplier_name || "").slice(0, 40)}${isUnambiguous(en.result) ? " · eindeutig ✓" : ""} · wartet auf Prüfung`}
                                            {en.status === "booked" && <span className="text-green-700 font-bold">automatisch gebucht ({en.bookedInfo || "ok"})</span>}
                                            {en.status === "error" && (en.error || "Fehler")}
                                        </span>
                                        {(en.status === "error" || en.status === "booked") && (
                                            <button type="button" className="ml-auto text-[11px] underline text-gray-500 shrink-0"
                                                onClick={() => setQueue((q) => q.filter((x) => x.qid !== en.qid))}
                                                data-testid={`scan-queue-dismiss-${en.qid}`}>
                                                entfernen
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {scan && !result && queue.length === 0 && (
                            <p className="text-[11px] text-gray-500 mt-1">Du kannst schon den nächsten Beleg scannen oder ablegen — er wird parallel analysiert, während du hier prüfst.</p>
                        )}
                    </div>
                )}
                {!scan && (
                    <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="scan-upload-block">
                        <h2 className="font-bold text-base mb-3 flex items-center gap-2"><Upload className="w-4 h-4" /> Beleg hochladen, aufnehmen oder per Handy scannen</h2>

                        {/* Drag & Drop Zone */}
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickDropped(e.dataTransfer.files); }}
                            onClick={() => fileInputRef.current?.click()}
                            className={`cursor-pointer rounded-sm border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "border-[#FFCC00] bg-[#FFFDF5]" : "border-gray-300 hover:border-[#FFCC00] hover:bg-gray-50"}`}
                            data-testid="scan-dropzone"
                        >
                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm font-bold text-[#111827]">Datei hierher ziehen oder klicken zum Auswählen</p>
                            <p className="text-[11px] text-gray-500 mt-1">PDF oder Bild (PNG/JPG/WEBP) · max 10 MB</p>
                            {file && busy && (
                                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="scan-selected-file-analyzing">
                                    <Loader2 className="w-3 h-3 animate-spin" /> {file.name} — wird analysiert…
                                </p>
                            )}
                            {file && !busy && scanError && (
                                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1" data-testid="scan-selected-file-error">
                                    <AlertCircle className="w-3 h-3" /> {file.name} — {scanError}
                                </p>
                            )}
                            {file && !busy && !scanError && (
                                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-sm px-2 py-1" data-testid="scan-selected-file">
                                    <Check className="w-3 h-3" /> {file.name}
                                </p>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                                onChange={(e) => { pickDropped(e.target.files); e.target.value = ""; }}
                                className="hidden"
                                data-testid="scan-file-input"
                            />
                            <p className="text-[10px] text-gray-400 mt-2">Sammel-Modus: mehrere Dateien auf einmal wählen oder nacheinander scannen — der Stapel wird parallel analysiert.</p>
                        </div>

                        <div className="flex flex-wrap gap-3 items-center mt-4">
                            <Button
                                onClick={() => bridge.start()}
                                disabled={busy || bridge.scanning}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                                data-testid="direct-scan-btn"
                            >
                                {bridge.scanning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1.5" />}
                                {bridge.scanning ? "Scanner arbeitet…" : "Mit PC scannen"}
                            </Button>
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy}
                                variant="outline"
                                className="rounded-sm uppercase tracking-wider border-2 border-[#111827] font-bold"
                                data-testid="scan-upload-btn"
                            >
                                <Upload className="w-4 h-4 mr-1.5" /> Datei hochladen
                            </Button>
                            {busy && (
                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500" data-testid="scan-busy-indicator">
                                    <Loader2 className="w-4 h-4 animate-spin" /> KI analysiert den Beleg…
                                </span>
                            )}
                        </div>
                        <ScanProgress active={busy} fileName={file?.name} phase={scanPhase} pages={scanPages} testId="delivery-scan-progress" />
                        <ScanPagesPreview file={file} testId="delivery-scan-pages" />
                        <label className="flex items-center gap-2 text-[11px] text-gray-700 mt-2 cursor-pointer select-none" data-testid="delivery-scan-duplex-toggle">
                            <input
                                type="checkbox"
                                checked={duplexScan}
                                onChange={toggleDuplex}
                                className="w-3.5 h-3.5 accent-[#111827]"
                                data-testid="delivery-scan-duplex-checkbox"
                            />
                            <span>Beidseitig scannen (Duplex) — Vorder- <b>und</b> Rückseite in einem Durchgang</span>
                        </label>
                        {(bridge.scanning || folderWaiting) && (
                            <div className="flex items-center justify-between gap-2 bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 rounded-sm mt-2" data-testid="delivery-scan-waiting">
                                <span className="text-sm text-emerald-900 font-semibold animate-pulse">
                                    {bridge.scanning
                                        ? "Scanner wird direkt angesteuert — bitte warten…"
                                        : "Warte auf Scan… Jetzt am Scanner / in der Scan-Software auf „Scannen“ drücken."}
                                </span>
                                {folderWaiting && (
                                    <button type="button" onClick={() => { folderAbortRef.current?.abort(); setFolderWaiting(false); }}
                                        className="text-xs font-bold text-emerald-800 underline shrink-0" data-testid="delivery-scan-folder-cancel">
                                        Abbrechen
                                    </button>
                                )}
                            </div>
                        )}
                        {bridge.online === true && (
                            <p className="text-[11px] mt-1.5 inline-flex items-center gap-2.5 flex-wrap" data-testid="direct-scan-bridge-online">
                                <span className="inline-flex items-center gap-1.5 font-bold text-green-700">
                                    <Check className="w-3.5 h-3.5" /> Scan-Bridge verbunden — Scanner bereit
                                </span>
                                {/* Nov-2026 (User-Wunsch): „Scanner reparieren" als dezenter Text statt Button */}
                                <button type="button" onClick={() => bridge.repair()} disabled={busy || bridge.scanning || bridge.repairing}
                                    className="inline-flex items-center gap-1 text-amber-700 underline hover:text-amber-900 disabled:opacity-50"
                                    title={'Behebt „Das WIA-Gerät ist ausgelastet": gibt hängende Scanner-Verbindungen frei und startet den Windows-Scandienst neu'}
                                    data-testid="direct-scan-repair-btn">
                                    {bridge.repairing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                                    {bridge.repairing ? "repariert…" : "Scanner reparieren"}
                                </button>
                                {!bridge.savedName && (
                                    <button type="button" onClick={() => bridge.start(true)} disabled={busy || bridge.scanning}
                                        className="text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
                                        title="Verfügbare Scanner der Scan-Bridge anzeigen und auswählen"
                                        data-testid="direct-scan-pick-device">
                                        Scanner wählen
                                    </button>
                                )}
                            </p>
                        )}
                        {bridge.online === false && (
                            <p className="text-[11px] font-bold text-red-600 mt-1.5 inline-flex items-center gap-1.5" data-testid="direct-scan-bridge-offline">
                                <AlertCircle className="w-3.5 h-3.5" /> Scan-Bridge nicht erreichbar — läuft sie auf diesem PC?
                            </p>
                        )}
                        {bridge.savedName && (
                            <p className="text-[11px] text-gray-500 mt-1.5" data-testid="direct-scan-device-info">
                                Standard-Scanner: <b>{bridge.savedName}</b>{" "}
                                <button type="button" onClick={() => bridge.start(true)} className="underline hover:text-gray-700" data-testid="direct-scan-change-device">Ändern</button>
                            </p>
                        )}
                        {Array.isArray(bridge.devices) && (
                            <div className="mt-2 border border-gray-200 rounded-sm bg-gray-50 p-3" data-testid="direct-scan-device-picker">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1.5">Scanner wählen (wird gemerkt)</p>
                                <div className="flex flex-wrap gap-2">
                                    {bridge.devices.map((d) => (
                                        <button key={d.id} type="button"
                                            onClick={() => { bridge.saveDevice(d); bridge.scanWith(d.id); }}
                                            className="text-xs font-bold bg-white border border-gray-300 rounded-sm px-2.5 py-1.5 hover:border-[#FFCC00]"
                                            data-testid={`direct-scan-device-${d.id}`}>
                                            <Printer className="w-3 h-3 inline mr-1" />{d.name || d.id}
                                        </button>
                                    ))}
                                    <button type="button" onClick={() => bridge.setDevices(null)} className="text-xs text-gray-500 underline" data-testid="direct-scan-picker-cancel">Abbrechen</button>
                                </div>
                            </div>
                        )}
                        {bridge.bridgeMissing && (
                            <div className="bg-red-50 border-l-4 border-red-500 px-3 py-2.5 rounded-sm mt-2 space-y-1.5" data-testid="direct-scan-bridge-hint">
                                <div className="text-xs font-bold text-red-900">Scan-Bridge nicht erreichbar — der Scanner kann nicht direkt angesteuert werden.</div>
                                <ol className="text-[11px] text-red-800 list-decimal ml-4 space-y-0.5">
                                    <li><a href="/TwoWheels-ScanBridge.cmd" download className="font-bold underline" data-testid="direct-scan-bridge-download">TwoWheels-ScanBridge.cmd herunterladen</a> — braucht <b>keine Admin-Rechte</b></li>
                                    <li>Doppelklick auf die Datei. Falls Windows-SmartScreen erscheint: „Weitere Informationen" → „Trotzdem ausführen"</li>
                                    <li>Die Bridge richtet sich automatisch im Windows-Autostart ein und läuft unsichtbar im Hintergrund</li>
                                    <li>Dann hier erneut auf „Mit PC scannen" klicken — der Scanner startet direkt</li>
                                </ol>
                                <div className="flex gap-3 pt-0.5">
                                    <button type="button" onClick={() => bridge.start()} className="text-xs font-bold underline text-red-900" data-testid="direct-scan-bridge-retry">Erneut prüfen</button>
                                    <button type="button" onClick={startFolderScan} className="text-xs underline text-red-800" data-testid="direct-scan-folder-fallback">Stattdessen Scan-Ordner überwachen</button>
                                </div>
                            </div>
                        )}
                        <ScannerSetupHelp onConfigureScanner={() => setDeskModal(true)} deskScannerName={deskScanner?.enabled ? deskScanner?.name : ""} />
                    </div>
                )}

                {scan && !result && scan.own_invoice && (
                    <div className="bg-white border-2 border-emerald-300 rounded-sm p-5 space-y-2" data-testid="scan-own-invoice-block">
                        <p className="font-black text-sm text-emerald-900 flex items-center gap-2">
                            <Check className="w-4 h-4" /> Eigene Rechnung erkannt: {scan.own_invoice.invoice_number}
                        </p>
                        <p className="text-[12px] text-emerald-900">
                            Dieser Beleg ist Ihre eigene Ausgangsrechnung{scan.own_invoice.customer_name ? <> an <b>{scan.own_invoice.customer_name}</b></> : null} —
                            er wird <b>nicht</b> als Wareneingang oder Eingangsrechnung gebucht.
                        </p>
                        {scan.own_invoice_result?.sent ? (
                            <p className="text-[12px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 inline-flex items-center gap-1.5" data-testid="scan-own-invoice-sent">
                                <Check className="w-3.5 h-3.5" /> An den Kunden versendet ({scan.own_invoice_result.sent_to})
                            </p>
                        ) : (
                            <p className="text-[12px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 inline-flex items-center gap-1.5" data-testid="scan-own-invoice-not-sent">
                                <AlertCircle className="w-3.5 h-3.5" /> Nicht an den Kunden versendet{scan.own_invoice_result?.reason ? `: ${scan.own_invoice_result.reason}` : ""}
                            </p>
                        )}
                        <div>
                            <Button onClick={reset} variant="outline" size="sm" className="rounded-sm" data-testid="scan-own-invoice-reset">
                                <XIcon className="w-3 h-3 mr-1" /> Nächsten Beleg scannen
                            </Button>
                        </div>
                    </div>
                )}
                {scan && !result && scan.maintenance_coupon && (
                    <div className="bg-white border-2 border-sky-300 rounded-sm p-5 space-y-2" data-testid="scan-coupon-block">
                        <p className="font-black text-sm text-sky-900 flex items-center gap-2">
                            <Check className="w-4 h-4" /> Wartungscoupon erkannt{scan.maintenance_coupon.model ? `: ${scan.maintenance_coupon.model}` : ""}
                        </p>
                        <p className="text-[12px] text-sky-900">
                            {scan.maintenance_coupon.vin && <>FIN <b>{scan.maintenance_coupon.vin}</b> · </>}
                            {scan.maintenance_coupon.km ? <>Km-Stand <b>{scan.maintenance_coupon.km}</b> · </> : null}
                            {scan.maintenance_coupon.service_date && <>Ausführung <b>{scan.maintenance_coupon.service_date}</b> · </>}
                            {scan.maintenance_coupon.customer_name
                                ? <>Kunde: <b>{scan.maintenance_coupon.customer_name}</b>{scan.maintenance_coupon.matched_by ? ` (über ${scan.maintenance_coupon.matched_by})` : ""}</>
                                : <>Kein Kunde zugeordnet</>}
                            {" — "}kein Wareneingang, keine Buchhaltung.
                        </p>
                        {scan.coupon_result?.sent ? (
                            <p className="text-[12px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 inline-flex items-center gap-1.5" data-testid="scan-coupon-sent">
                                <Check className="w-3.5 h-3.5" /> {scan.coupon_result?.queued
                                    ? <>Am Fahrzeug gespeichert — Versand an {scan.coupon_result.sent_to} heute Abend gebündelt (1 E-Mail pro Kunde)</>
                                    : <>An den Kunden versendet ({scan.coupon_result.sent_to})</>}
                            </p>
                        ) : (
                            <p className="text-[12px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 inline-flex items-center gap-1.5" data-testid="scan-coupon-not-sent">
                                <AlertCircle className="w-3.5 h-3.5" /> Nicht versendet{scan.coupon_result?.reason ? `: ${scan.coupon_result.reason}` : ""}
                            </p>
                        )}
                        <div>
                            <Button onClick={reset} variant="outline" size="sm" className="rounded-sm" data-testid="scan-coupon-reset">
                                <XIcon className="w-3 h-3 mr-1" /> Nächsten Beleg scannen
                            </Button>
                        </div>
                    </div>
                )}
                {scan && !result && !scan.own_invoice && !scan.maintenance_coupon && (
                    <>
                        {scan.auto_booked && (
                            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-sm p-4" data-testid="scan-autobooked-banner">
                                <p className="font-black text-sm text-emerald-900 flex items-center gap-2">
                                    <Check className="w-4 h-4" /> Bereits automatisch verbucht — kein weiterer Klick nötig
                                </p>
                                <p className="text-[12px] text-emerald-900 mt-1">
                                    Dieser Beleg wurde direkt nach der KI-Analyse automatisch ins Lager gebucht
                                    ({scan.auto_book_summary?.booked ?? 0} Position(en) zugebucht, {scan.auto_book_summary?.created ?? 0} Artikel neu angelegt).
                                    Die Ansicht unten dient nur noch der Kontrolle — ein erneutes Buchen würde den Bestand doppeln.
                                    (Automatik abschaltbar: Warenwirtschaft → Zahnrad → „Scans automatisch verbuchen".)
                                </p>
                            </div>
                        )}
                        {scan.auto_booked === false && scan.auto_book_error && !scan.possible_duplicate && (
                            <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-[12px] text-amber-900" data-testid="scan-autobook-error">
                                <b>Automatische Verbuchung nicht möglich:</b> {scan.auto_book_error} — bitte unten prüfen und ggf. manuell buchen.
                            </div>
                        )}
                        <div className="bg-white border border-gray-200 rounded-sm p-5 space-y-3" data-testid="scan-meta-block">
                            <h2 className="font-bold text-base flex items-center justify-between flex-wrap gap-2">
                                <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />Erkannte Daten</span>
                                {scan.supplier_account_match && (
                                    <span className="text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-2 py-1 inline-flex items-center gap-1.5" data-testid="supplier-account-match">
                                        <Check className="w-3.5 h-3.5" />
                                        Kd-Nr. {scan.supplier_account_match.customer_number} erkannt → {scan.supplier_account_match.supplier_name}
                                        {scan.supplier_account_match.dealer_name && <> · zugeordnet: {scan.supplier_account_match.dealer_name}{scan.supplier_account_match.site_code ? ` (${scan.supplier_account_match.site_code})` : ""}</>}
                                    </span>
                                )}
                                {!scan.supplier_account_match && scan.customer_number && (
                                    <span className="inline-flex items-center gap-2 flex-wrap">
                                        <span className="text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 rounded-sm px-2 py-1" data-testid="supplier-account-no-match">
                                            Kd-Nr. {scan.customer_number} erkannt — kein Lieferant mit dieser Kundennummer hinterlegt
                                        </span>
                                        <button
                                            type="button"
                                            onClick={createSupplierFromScan}
                                            disabled={creatingSupplier}
                                            className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#111827] text-[#FFCC00] hover:bg-black rounded-sm px-2 py-1 disabled:opacity-50"
                                            data-testid="create-supplier-from-scan-btn"
                                        >
                                            {creatingSupplier ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                            Als Lieferant mit dieser Kd-Nr. anlegen
                                        </button>
                                    </span>
                                )}
                                {scan.confidence !== undefined && (
                                    <span className="text-xs font-normal text-gray-500">
                                        Vertrauen: <strong className="font-mono-num">{Math.round(scan.confidence * 100)}%</strong>
                                        {scan.mocked && <span className="ml-2 text-amber-700">· Fallback</span>}
                                    </span>
                                )}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Lieferant</label>
                                    <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Lieferant Firmenname" className="rounded-sm mt-1" data-testid="scan-supplier-name" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Empfänger / Firmierung</label>
                                    <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Warenempfänger" className="rounded-sm mt-1" data-testid="scan-recipient-name" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Lieferschein-Nr.</label>
                                    <Input value={dnNumber} onChange={(e) => setDnNumber(e.target.value)} placeholder="LS-2026-001" className="rounded-sm mt-1" data-testid="scan-dn-number" />
                                </div>
                            </div>
                            {orgSites.length > 1 && (
                                <div className="mt-1 border-t border-gray-100 pt-3" data-testid="scan-target-site-block">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Buchen für Standort</label>
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        <select value={targetDealerId} onChange={(e) => setTargetDealerId(e.target.value)}
                                            className="w-full md:w-96 border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white"
                                            data-testid="scan-target-site-select">
                                            {orgSites.map((s) => (
                                                <option key={s.id} value={s.id}>{s.role}: {s.label}</option>
                                            ))}
                                        </select>
                                        {scan.supplier_account_match?.dealer_id && targetDealerId === scan.supplier_account_match.dealer_id && (
                                            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1" data-testid="scan-target-site-auto">
                                                automatisch über Kd-Nr. zugeordnet
                                            </span>
                                        )}
                                        {scan.supplier_account_match?.dealer_id && targetDealerId !== scan.supplier_account_match.dealer_id && (
                                            <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="scan-target-site-changed">
                                                manuell geändert (erkannt: {scan.supplier_account_match.dealer_name || scan.supplier_account_match.site_code || "anderer Standort"})
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-1">Wareneingang, Artikel und Buchhaltungs-Beleg werden diesem Standort zugeordnet.</p>
                                </div>
                            )}
                        </div>

                        {/* Dokumenttyp + Doppel-Scan-Warnung */}
                        <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-3" data-testid="scan-doctype-block">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Belegtyp</label>
                                <select value={docType} onChange={(e) => setDocType(e.target.value)}
                                    className="mt-1 w-full md:w-80 border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white"
                                    data-testid="scan-doctype">
                                    <option value="delivery_note">Lieferschein (nur Wareneingang)</option>
                                    <option value="invoice">Rechnung (Wareneingang + Buchhaltung)</option>
                                    <option value="combined">Lieferschein = Rechnung (Wareneingang + Buchhaltung)</option>
                                    <option value="control_only">Nur zur Rechnungskontrolle (kein Wareneingang, z. B. TÜV)</option>
                                </select>
                                <p className="text-[11px] text-gray-500 mt-1">
                                    {docType === "control_only"
                                        ? "Keine Lager-Buchung, keine Artikel-Anlage — Beleg wird der Buchhaltung zum Abgleich mit der eingehenden Rechnung bereitgestellt."
                                        : docType === "delivery_note"
                                        ? "Nur Lager-Buchung. Keine Weitergabe an die Buchhaltung."
                                        : commissionMode
                                            ? "Kommission: Rechnung geht in die Buchhaltung der Ziel-Firma (nicht Ihre). Beleg bleibt im Lieferschein-Listing."
                                            : "Wird als Eingangsrechnung (Kreditor) an Ihre Buchhaltung übergeben."}
                                </p>
                                {scan.service_provider && (
                                    <p className="text-[11px] font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-2 py-1.5 mt-1.5 inline-flex items-center gap-1.5" data-testid="scan-service-provider-hint">
                                        <AlertCircle className="w-3.5 h-3.5" /> Dienstleister/Prüforganisation erkannt (z. B. TÜV) — Belegtyp automatisch auf „Nur zur Rechnungskontrolle" gesetzt.
                                    </p>
                                )}
                                {(scan.tuev_actions?.length ? scan.tuev_actions : (scan.tuev_action ? [scan.tuev_action] : [])).map((ta, ti) => (
                                    <p key={ti} className={`text-[11px] font-bold rounded-sm px-2 py-1.5 mt-1.5 inline-flex items-center gap-1.5 border ${ta.added ? "text-green-800 bg-green-50 border-green-200" : "text-amber-800 bg-amber-50 border-amber-200"}`} data-testid={ti === 0 ? "scan-tuev-action" : `scan-tuev-action-${ti}`}>
                                        <Wrench className="w-3.5 h-3.5" />
                                        {ta.added
                                            ? `TÜV-Position „${ta.position?.description}" automatisch zu Auftrag ${ta.work_order_number} (${ta.customer_name}) hinzugefügt — erkannt über ${ta.matched_by}.`
                                            : `TÜV-Beleg erkannt${ta.vin ? ` (FIN ${ta.vin})` : ""}${ta.owner_last_name ? ` · Halter ${ta.owner_last_name}` : ""} — ${ta.reason}${ta.work_order_number ? ` (Auftrag ${ta.work_order_number})` : ""}.`}
                                    </p>
                                ))}
                                {(scan.tuev_vehicle_updates || []).map((vu, vi) => (
                                    <p key={`vu-${vi}`} className={`text-[11px] font-bold rounded-sm px-2 py-1.5 mt-1.5 inline-flex items-center gap-1.5 border ${vu.found ? "text-green-800 bg-green-50 border-green-200" : "text-amber-800 bg-amber-50 border-amber-200"}`} data-testid={`scan-tuev-vehicle-update-${vi}`}>
                                        <Wrench className="w-3.5 h-3.5" />
                                        {vu.found
                                            ? `Fahrzeug ${vu.vehicle_label || ""} (FIN ${vu.vin}) als ${vu.found === "kundenfahrzeug" ? "Kundenfahrzeug" : "Lagerfahrzeug"} gefunden — ${
                                                Object.keys(vu.updated || {}).length
                                                    ? "aktualisiert: " + [
                                                        vu.updated.hu_date ? `HU/TÜV bis ${vu.updated.hu_date.slice(0, 7)}` : null,
                                                        vu.updated.mileage ? `km-Stand ${Number(vu.updated.mileage).toLocaleString("de-DE")} km` : null,
                                                        vu.updated.license_plate ? `Kennzeichen ${vu.updated.license_plate} ergänzt` : null,
                                                    ].filter(Boolean).join(" · ")
                                                    : "keine neueren Daten auf dem Beleg (nichts geändert)"}${vu.archived ? " · Bescheinigung im Fahrzeug-Archiv abgelegt" : ""}.`
                                            : `Fahrzeug zur FIN ${vu.vin}${vu.license_plate ? ` / ${vu.license_plate}` : ""} wurde NICHT im System gefunden — HU/km/Kennzeichen konnten nicht aktualisiert werden.`}
                                    </p>
                                ))}
                                {(scan.vehicle_vins?.length || 0) > 1 && (
                                    <p className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 mt-1.5" data-testid="scan-vehicle-vins">
                                        <b>{scan.vehicle_vins.length} Fahrzeuge auf dem Beleg erkannt:</b>{" "}
                                        <span className="font-mono-num">{scan.vehicle_vins.join(" · ")}</span>
                                    </p>
                                )}
                                {scan.non_stock && (
                                    <p className="text-[11px] font-bold text-orange-800 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1.5 mt-1.5 inline-flex items-center gap-1.5" data-testid="scan-non-stock-hint">
                                        <AlertCircle className="w-3.5 h-3.5" /> Werkzeug/Werkstatt-/Bürobedarf erkannt ({scan.non_stock.reason}) — Positionen werden NICHT in den Bestand gebucht, der Beleg liegt in der Buchhaltung bereit.
                                    </p>
                                )}
                                {scan.buchhaltung_routed && !scan.possible_duplicate && (
                                    <p className="text-[11px] font-bold text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-sm px-2 py-1.5 mt-1.5 inline-flex items-center gap-1.5" data-testid="scan-buchhaltung-routed">
                                        <FileText className="w-3.5 h-3.5" />
                                        {scan.buchhaltung_routed.is_own
                                            ? `Beleg wurde der eigenen Buchhaltung bereitgestellt (${scan.buchhaltung_routed.reason}).`
                                            : `Beleg gehört zu ${scan.buchhaltung_routed.dealer_name}${scan.buchhaltung_routed.site_code ? ` (${scan.buchhaltung_routed.site_code})` : ""} — automatisch in dessen Buchhaltungs-Eingang gelegt.`}
                                    </p>
                                )}
                                {scan.warranty?.is_ceva && <CevaWarrantyPanel scan={scan} />}
                                {scan.commission_order_match && (
                                    <CommissionOrderPanel scan={scan} rows={rows}
                                        onRejectAll={() => setRows((prev) => prev.map((r) => ({ ...r, toOrder: false })))}
                                        onAcceptAll={() => setRows((prev) => prev.map((r) => ({ ...r, toOrder: true })))} />
                                )}
                            </div>
                            {scan.possible_duplicate && (
                                <div className="bg-red-50 border border-red-300 rounded-sm p-3" data-testid="scan-duplicate-warning">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                                        <p className="text-[12px] text-red-800">
                                            <b>Beleg wurde bereits gescannt{scan.possible_duplicate.source === "buchhaltung_inbox" ? " und der Buchhaltung bereitgestellt" : " und verbucht"}!</b>{" "}
                                            {scan.possible_duplicate.supplier_name} {scan.possible_duplicate.delivery_note_number} ({(scan.possible_duplicate.created_at || "").slice(0, 10)})
                                            {scan.possible_duplicate.scanned_by_site ? ` · erfasst von ${scan.possible_duplicate.scanned_by_site}` : ""}.
                                            {" "}Es wurde <b>nichts doppelt gebucht</b>.
                                            {scan.buchhaltung_routed && !scan.buchhaltung_routed.is_own
                                                ? ` Der Beleg gehört zu ${scan.buchhaltung_routed.dealer_name}${scan.buchhaltung_routed.site_code ? ` (${scan.buchhaltung_routed.site_code})` : ""} und liegt bereits in dessen Buchhaltungs-Eingang.`
                                                : scan.buchhaltung_routed ? " Der Beleg liegt bereits in der Buchhaltung." : ""}
                                        </p>
                                    </div>
                                    <button type="button" onClick={discardScan}
                                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded-sm px-3 py-1.5"
                                        data-testid="scan-duplicate-discard-btn">
                                        <XIcon className="w-3.5 h-3.5" /> Scan verwerfen — neuen Beleg scannen
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Kommissionsware-Erkennung anhand Firmierung */}
                        <div className={`rounded-sm border p-4 ${decisionRequired && !decisionMade ? "bg-red-50 border-red-300" : commissionMode ? "bg-amber-50 border-amber-300" : "bg-white border-gray-200"}`} data-testid="scan-commission-block">
                            {decisionRequired && !decisionMade && (
                                <div className="mb-3" data-testid="scan-commission-decision">
                                    <p className="text-sm font-bold text-red-800 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" /> Bitte entscheiden – Firmierung weicht ab
                                    </p>
                                    <p className="text-[12px] text-red-800 mt-1">
                                        Empfänger laut Beleg: <b>„{recipientName || "nicht erkannt"}"</b>
                                        {scan.own_firmierung ? <> · Ihre Firmierung: <b>„{scan.own_firmierung}"</b></> : null}.
                                        {scan.recipient_match === "unknown"
                                            ? " Der Empfänger konnte nicht sicher gelesen werden."
                                            : " Der Empfänger entspricht nicht Ihrer Firmierung."}
                                        {" "}Wie soll die Ware gebucht werden?
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        <Button type="button" onClick={() => { setCommissionMode(false); setDecisionMade(true); }}
                                            className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="scan-decision-own">
                                            In eigenes Lager
                                        </Button>
                                        <Button type="button" onClick={() => { setCommissionMode(true); setDecisionMade(true); }}
                                            className="bg-amber-500 hover:bg-amber-600 text-[#111827] font-bold rounded-sm" data-testid="scan-decision-commission">
                                            Als Kommissionsware
                                        </Button>
                                    </div>
                                </div>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={commissionMode} onChange={(e) => { setCommissionMode(e.target.checked); setDecisionMade(true); }} data-testid="scan-commission-toggle" className="w-4 h-4" />
                                <span className="font-bold text-sm flex items-center gap-2">
                                    <AlertCircle className={`w-4 h-4 ${commissionMode ? "text-amber-600" : "text-gray-400"}`} />
                                    Kommissionsware (Empfänger-Firmierung weicht ab)
                                </span>
                            </label>
                            {scan.is_commission && (
                                <p className="text-[11px] text-amber-800 mt-1.5" data-testid="scan-commission-hint">
                                    Automatisch erkannt: Empfänger „{recipientName || "—"}" entspricht nicht Ihrer eigenen Firmierung → als Kommissionsware behandeln.
                                </p>
                            )}
                            {commissionMode && (
                                <div className="mt-3">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Ziel (Konsignant)</label>
                                    <select
                                        value={commissionTarget ? `${commissionTarget.type}:${commissionTarget.id}` : ""}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (!v) { setCommissionTarget(null); return; }
                                            const found = (scan.commission_suggestions || []).find((s) => `${s.type}:${s.id}` === v);
                                            setCommissionTarget(found || null);
                                        }}
                                        className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white"
                                        data-testid="scan-commission-target"
                                    >
                                        {(scan.commission_suggestions || []).map((s) => (
                                            <option key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>
                                                {s.name} ({s.type === "dealer" ? "Händler" : "Lieferant"})
                                            </option>
                                        ))}
                                        <option value="">→ In Konsignationsverwaltung buchen (kein Ziel)</option>
                                    </select>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        {commissionTarget
                                            ? `Ware wird ${commissionTarget.name} als Kommission zugeordnet.`
                                            : "Ohne Ziel-Auswahl wird die Ware in die Konsignationsverwaltung gebucht."}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Aug-2026 (User-Wunsch): 409-Duplikat beim Buchen → klare
                            Meldung + „Scan verwerfen" statt kryptischem Fehler */}
                        {dupBlock && (
                            <div className="bg-red-50 border-2 border-red-400 rounded-sm p-5" data-testid="scan-dup-block">
                                <p className="font-black text-sm text-red-800 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" /> Beleg wurde bereits gescannt und verbucht!
                                </p>
                                <p className="text-[12px] text-red-800 mt-1" data-testid="scan-dup-block-details">
                                    {dupBlock.supplier_name || "Lieferant unbekannt"}
                                    {dupBlock.delivery_note_number ? ` · Beleg-Nr. ${dupBlock.delivery_note_number}` : ""}
                                    {dupBlock.created_at ? ` · erfasst am ${(dupBlock.created_at || "").slice(0, 10)}` : ""}
                                    {dupBlock.scanned_by_site ? ` · von ${dupBlock.scanned_by_site}` : ""}.
                                    {" "}Eine erneute Buchung würde den Vorgang doppeln.
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button onClick={discardScan} className="bg-red-600 hover:bg-red-700 text-white font-black rounded-sm uppercase tracking-wider" data-testid="scan-dup-discard-btn">
                                        <XIcon className="w-3.5 h-3.5 mr-1" /> Scan verwerfen — neuen Beleg scannen
                                    </Button>
                                </div>
                            </div>
                        )}

                        {docType === "control_only" ? (
                            <div className="bg-sky-50 border-2 border-sky-300 rounded-sm p-5" data-testid="scan-control-only-block">
                                <p className="font-bold text-sm text-sky-900 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Nur zur Rechnungskontrolle — keine Artikel-Anlage</p>
                                <p className="text-[12px] text-sky-900 mt-1">
                                    Dieser Beleg (z. B. TÜV/Prüforganisation) wird <b>nicht</b> als Wareneingang gebucht und es werden keine
                                    Artikelnummern angelegt. Er wird der Buchhaltung bereitgestellt, um die später eingehende Rechnung
                                    abzugleichen (Buchhaltung → Kreditoren → „Rechnungskontrolle").
                                    {rows.length > 0 ? ` ${rows.length} erkannte Position(en) werden nur dokumentiert.` : ""}
                                </p>
                                <Button onClick={() => onCommit()} disabled={busy} className="mt-3 bg-[#111827] hover:bg-black text-[#FFCC00] font-black rounded-sm uppercase tracking-wider" data-testid="scan-commit-control-btn">
                                    {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                    Für Rechnungskontrolle ablegen
                                </Button>
                            </div>
                        ) : (
                        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="scan-rows-block">
                            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                                <div className="font-bold text-base">{rows.length} Position(en)</div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Nov-2026 (User-Wunsch) — Umschalter: Kommission ⇄ eigener Warenbestand */}
                                <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden" data-testid="scan-mode-switch" title="Klick zum Wechseln: eigenes Lager oder Kommissionsware">
                                    <button type="button" onClick={() => { setCommissionMode(false); setDecisionMade(true); }}
                                        className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${!commissionMode ? "bg-[#111827] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                                        data-testid="scan-mode-own">
                                        Eigenes Lager
                                    </button>
                                    <button type="button" onClick={() => { setCommissionMode(true); setDecisionMade(true); }}
                                        className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${commissionMode ? "bg-amber-500 text-[#111827]" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                                        data-testid="scan-mode-commission">
                                        Kommission
                                    </button>
                                </div>
                                {scan.auto_booked ? (
                                    changedRows.length > 0 ? (
                                        <Button onClick={bookCorrections} disabled={correcting}
                                            className="bg-amber-500 hover:bg-amber-600 text-[#111827] font-black rounded-sm uppercase tracking-wider"
                                            data-testid="scan-corrections-btn">
                                            {correcting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                            Änderungen speichern & Korrektur buchen ({changedRows.length})
                                        </Button>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-sm px-2.5 py-1.5" data-testid="scan-already-booked-badge">
                                            <Check className="w-3.5 h-3.5" /> Bereits automatisch verbucht
                                        </span>
                                    )
                                ) : scan.possible_duplicate ? (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider bg-red-100 text-red-800 border border-red-300 rounded-sm px-2.5 py-1.5" data-testid="scan-duplicate-badge">
                                        <AlertCircle className="w-3.5 h-3.5" /> Bereits verbucht (Doppel-Scan) — keine erneute Buchung
                                    </span>
                                ) : (
                                <Button onClick={onCommit} disabled={busy || (decisionRequired && !decisionMade)}
                                    className="bg-[#111827] hover:bg-black text-[#FFCC00] border-2 border-[#FFCC00] font-black rounded-sm uppercase tracking-wider disabled:opacity-50"
                                    data-testid="scan-commit-btn">
                                    {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                    {decisionRequired && !decisionMade ? "Erst Wahl treffen" : "Ins Lager buchen"}
                                </Button>
                                )}
                            </div>
                            </div>
                            <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[640px]">
                                <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                    <tr>
                                        <th className="text-left px-3 py-2">SKU</th>
                                        <th className="text-left px-3 py-2">Bezeichnung</th>
                                        <th className="text-right px-3 py-2">Menge</th>
                                        <th className="text-left px-3 py-2 w-16">Einh.</th>
                                        <th className="text-right px-3 py-2">EK-€</th>
                                        <th className="text-right px-3 py-2" title="Unverbindliche Preisempfehlung (brutto) vom Beleg">UVP-€</th>
                                        <th className="text-left px-3 py-2">Match</th>
                                        <th className="text-left px-3 py-2">Aktion</th>
                                        {(scan.commission_order_match?.orders?.length > 0) && (
                                            <th className="text-left px-3 py-2" title="Diese Position dem erkannten Werkstatt-Auftrag zubuchen oder ablehnen">Auftrag</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.idx} className="border-t border-gray-100" data-testid={`scan-row-${r.idx}`}>
                                            <td className="px-3 py-2">
                                                <Input value={r.sku || ""} onChange={(e) => updateRow(r.idx, { sku: e.target.value })} className="text-xs rounded-sm h-7 px-1.5 w-28" data-testid={`scan-sku-${r.idx}`} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input value={r.name} onChange={(e) => updateRow(r.idx, { name: e.target.value })} className="text-xs rounded-sm h-7 px-1.5" data-testid={`scan-name-${r.idx}`} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input type="number" min="0" step="0.01" value={r.quantity} onChange={(e) => updateRow(r.idx, { quantity: e.target.value })} className="text-xs rounded-sm h-7 px-1.5 w-20 text-right font-mono-num" data-testid={`scan-qty-${r.idx}`} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input value={r.unit} onChange={(e) => updateRow(r.idx, { unit: e.target.value })} className="text-xs rounded-sm h-7 px-1.5 w-14" />
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input type="number" min="0" step="0.01" value={r.purchase_price_net} onChange={(e) => updateRow(r.idx, { purchase_price_net: e.target.value })} className="text-xs rounded-sm h-7 px-1.5 w-24 text-right font-mono-num" />
                                            </td>
                                            <td className="px-3 py-2">
                                                <Input type="number" min="0" step="0.01" value={r.uvp_gross ?? ""} onChange={(e) => updateRow(r.idx, { uvp_gross: e.target.value })} placeholder="—" title="UVP brutto vom Beleg — wird als Verkaufspreis übernommen, wenn keiner hinterlegt ist" className="text-xs rounded-sm h-7 px-1.5 w-24 text-right font-mono-num" data-testid={`scan-uvp-${r.idx}`} />
                                            </td>
                                            <td className="px-3 py-2 text-[11px]">
                                                {r._matchInfo ? (
                                                    <span className="inline-flex items-center gap-1 bg-green-50 text-green-900 px-2 py-0.5 rounded-sm font-bold">
                                                        <Check className="w-3 h-3" /> {r._matchInfo.score}% · {r._matchInfo.sku}
                                                    </span>
                                                ) : (
                                                    <span className="text-amber-700 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> wird neu angelegt</span>
                                                )}
                                                {r._backorder && (
                                                    <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-900 border border-orange-300 px-2 py-0.5 rounded-sm font-bold ml-1" title={r._reorder ? "Als Rückstand erkannt — steht bereits auf der Bestellliste" : "Als Rückstand erkannt (Liefermenge 0) — wird beim Buchen auf die Bestell-/Rückstandsliste gesetzt"} data-testid={`scan-backorder-badge-${r.idx}`}>
                                                        <AlertCircle className="w-3 h-3" /> Rückstand{r._reorder ? " · auf Bestellliste" : ""}
                                                    </span>
                                                )}
                                                {r._reorder && (
                                                    <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-900 px-2 py-0.5 rounded-sm font-bold ml-1" title={`Bestellte Menge: ${r._reorder.ordered_qty}${r._reorder.work_order_number ? " · Auftrag " + r._reorder.work_order_number : ""}`} data-testid={`scan-reorder-badge-${r.idx}`}>
                                                        <ShoppingCart className="w-3 h-3" /> bestellt
                                                    </span>
                                                )}
                                                {r._reorder?.work_order && (
                                                    <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-900 border border-violet-200 px-2 py-0.5 rounded-sm font-bold ml-1" title="Für diesen Werkstatt-Auftrag bestellt" data-testid={`scan-reorder-wo-${r.idx}`}>
                                                        <Wrench className="w-3 h-3" />
                                                        {[
                                                            [r._reorder.work_order.customer_first_name, r._reorder.work_order.customer_last_name].filter(Boolean).join(" "),
                                                            [r._reorder.work_order.vehicle_brand, r._reorder.work_order.vehicle_model].filter(Boolean).join(" ")
                                                                + (r._reorder.work_order.vehicle_color ? `, ${r._reorder.work_order.vehicle_color}` : ""),
                                                            r._reorder.work_order.order_number,
                                                        ].filter((s) => s && s.trim()).join(" · ")}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <select
                                                    value={r.action}
                                                    onChange={(e) => updateRow(r.idx, { action: e.target.value })}
                                                    className="border border-gray-300 rounded-sm px-1.5 py-1 text-[11px] bg-white"
                                                    data-testid={`scan-action-${r.idx}`}
                                                >
                                                    <option value="match" disabled={!r.article_id}>Zubuchen (vorhanden)</option>
                                                    <option value="create">Neu anlegen</option>
                                                    <option value="skip">Überspringen</option>
                                                </select>
                                            </td>
                                            {(scan.commission_order_match?.orders?.length > 0) && (
                                                <td className="px-3 py-2">
                                                    <div className="inline-flex rounded-sm border border-violet-300 overflow-hidden whitespace-nowrap" title="Diese Position dem erkannten Auftrag zubuchen oder ablehnen">
                                                        <button type="button" onClick={() => updateRow(r.idx, { toOrder: true })}
                                                            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${r.toOrder !== false ? "bg-violet-700 text-white" : "bg-white text-gray-400 hover:bg-violet-50"}`}
                                                            data-testid={`scan-toorder-yes-${r.idx}`}>
                                                            Zubuchen
                                                        </button>
                                                        <button type="button" onClick={() => updateRow(r.idx, { toOrder: false })}
                                                            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${r.toOrder === false ? "bg-red-600 text-white" : "bg-white text-gray-400 hover:bg-red-50"}`}
                                                            data-testid={`scan-toorder-no-${r.idx}`}>
                                                            Ablehnen
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                        )}
                        {docType !== "control_only" && rows.length > 0 && !scan.auto_booked && !scan.possible_duplicate && (
                            <div className="flex justify-end" data-testid="scan-commit-bottom-bar">
                                <Button onClick={() => onCommit()} disabled={busy || (decisionRequired && !decisionMade)} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-black rounded-sm uppercase tracking-wider disabled:opacity-50" data-testid="scan-commit-btn-bottom">
                                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                                    Artikel jetzt buchen ({rows.filter((r) => r.action !== "skip" && r.quantity > 0).length} Positionen)
                                </Button>
                            </div>
                        )}
                        {/* Aug-2026 (User-Vorschlag): nach Automatik-Verbuchung nur noch der Korrektur-Button */}
                        {docType !== "control_only" && scan.auto_booked && changedRows.length > 0 && (
                            <div className="flex justify-end" data-testid="scan-corrections-bottom-bar">
                                <Button onClick={bookCorrections} disabled={correcting}
                                    className="bg-amber-500 hover:bg-amber-600 text-[#111827] font-black rounded-sm uppercase tracking-wider"
                                    data-testid="scan-corrections-btn-bottom">
                                    {correcting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                                    Änderungen speichern & Korrektur buchen ({changedRows.length})
                                </Button>
                            </div>
                        )}
                        <div className="flex justify-end">
                            <Button onClick={reset} variant="outline" size="sm" className="rounded-sm" data-testid="scan-reset">
                                <XIcon className="w-3 h-3 mr-1" /> Andere Datei wählen
                            </Button>
                        </div>
                    </>
                )}

                {result && (
                    <div className="bg-white border border-green-200 rounded-sm p-5" data-testid="scan-result-block">
                        <div className="flex items-center gap-2 mb-3">
                            <Check className="w-5 h-5 text-green-700" />
                            <h2 className="font-bold text-base">Buchung abgeschlossen</h2>
                        </div>
                        {result.control_note_created && (
                            <p className="text-[12px] font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 mb-3" data-testid="scan-result-control-note">
                                Beleg wurde für die Buchhaltung zur Rechnungskontrolle abgelegt (Buchhaltung → Kreditoren → „Rechnungskontrolle"). Beim Eintreffen der Rechnung wird er automatisch abgeglichen.
                            </p>
                        )}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <ResultCard label="Bestand erhöht" value={result.count_booked} />
                            <ResultCard label="Neu angelegt" value={result.count_created} tone="success" />
                            <ResultCard label="Fehler" value={result.count_errors} tone={result.count_errors > 0 ? "warn" : "muted"} />
                        </div>
                        {(result.booked.length + result.created.length) > 0 && (
                            <ul className="border border-gray-200 rounded-sm divide-y mb-3">
                                {[...result.booked, ...result.created].map((b, i) => (
                                    <li key={i} className="px-3 py-2 text-xs flex items-center justify-between">
                                        <div>
                                            <span className="font-bold">{b.sku}</span>
                                            <span className="ml-2">{b.name}</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono-num font-bold text-green-700">+{b.qty_added}</div>
                                            <div className="text-[10px] text-gray-500">Stand: {b.stock_after}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {result.errors?.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                                <div className="text-[11px] font-bold uppercase text-amber-900 mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Fehler</div>
                                <ul className="text-[11px] text-amber-900 space-y-0.5">
                                    {result.errors.map((er, i) => <li key={i}>{er.name}: {er.error}</li>)}
                                </ul>
                            </div>
                        )}
                        <div className="mt-4 flex gap-2 justify-end">
                            <Button onClick={reset} variant="outline" className="rounded-sm" data-testid="scan-result-reset">
                                {queue.some((en) => ["running", "done"].includes(en.status))
                                    ? `Nächsten Beleg prüfen (${queue.filter((en) => ["running", "done"].includes(en.status)).length} im Stapel)`
                                    : "Weiteren Lieferschein scannen"}
                            </Button>
                            <Link to="/dealer/wms">
                                <Button className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider">
                                    Zur Warenwirtschaft →
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}

                <ScanHistory reload={historyReload} />
            </div>

            {camOpen && (
                <DocumentCameraCapture
                    onClose={() => setCamOpen(false)}
                    onCapture={(f) => { setCamOpen(false); enqueueFiles([f]); }}
                />
            )}
            <DeskScannerConfigModal open={deskModal} onClose={() => setDeskModal(false)} onSaved={(c) => setDeskScanner(c)} />
        </div>
    );
}

function ResultCard({ label, value, tone = "default" }) {
    const tones = {
        default: "bg-gray-50 border-gray-200",
        success: "bg-green-50 border-green-200",
        warn: "bg-amber-50 border-amber-200",
        muted: "bg-gray-50 border-gray-200 text-gray-500",
    };
    return (
        <div className={`border rounded-sm p-3 text-center ${tones[tone]}`}>
            <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
            <div className="text-2xl font-black font-mono-num">{value}</div>
        </div>
    );
}
